import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';

/**
 * The three connector landing pages under `/integrations/warehouse`. Mirrors
 * `WarehouseConnector` in the warehouse page folder, which cannot be imported
 * here: a feature must not depend on a page.
 *
 * This map lives with the catalog rather than in `features/warehouse-sync`,
 * where it started. `warehouse-sync` imports `IntegrationNames` from this
 * feature's barrel, so a dependency back the other way closed an import cycle
 * that fallow caught.
 */
type WarehouseConnectorPage = 'redshift' | 'bigquery' | 'snowflake';

/** Every segment `/integrations/warehouse/:segment` resolves to. */
export type WarehousePageSegment = WarehouseDestination | WarehouseConnectorPage;

/**
 * Backend catalog id → the warehouse page that entry belongs to.
 *
 * These six ids are the data-warehouse rows of the backend catalog (the
 * "spreadsheet" list). Two surfaces need to know about them:
 *
 * - the directory replaces them with its own cards, so the raw rows are
 *   filtered out of the backend list — that list is {@link WAREHOUSE_CATALOG_IDS};
 * - the catalog landing page builds their "Get started" button from the
 *   segments below.
 *
 * Only the id list is shared. The card's href is built separately, in
 * `directory/synthesize.ts` plus the directory's own `CONNECTOR_ROUTE_PATHS`,
 * and it targets the legacy `journeys/integrations/warehouse/<segment>` twin
 * rather than the migrated URL this map feeds. The two agree on which page each
 * integration belongs to, which is the point; they are not one code path.
 *
 * **The six are not uniform once a reader clicks.** `redshift`, `bigquery` and
 * `snowflake` have connector landing pages that deliberately carry no
 * warehouse-sync plan gate, so an account without the feature can still read the
 * upgrade pitch. The three storage destinations only have the `:type` config
 * route, which does carry that gate and redirects to the integrations index with
 * a "you don't have access" message. So for a non-premium account, Azure Blob
 * Storage's button explains the gate rather than showing a pitch. That is the
 * product's existing behaviour for the `:type` subtree and it is still an
 * improvement on the empty page this replaced, but it is a known asymmetry, not
 * card parity — the directory card shows an upgrade popover instead of
 * navigating. Revisit with FPS-3608's follow-up if the pitch matters more.
 *
 * Keyed on the catalog **id**, not the name. Both identify these rows — the id
 * is the name with spaces underscored — but the name is display text, and this
 * product already spells these differently across surfaces: the warehouse pages
 * call `s3` "Amazon Simple Storage Service / S3" where the catalog row is
 * "Amazon S3".
 *
 * A `Map` rather than an object because the lookup key is backend data. Indexing
 * a `Record<string, …>` would type a miss as a hit — `noUncheckedIndexedAccess`
 * is off — and `get` returns `undefined` honestly.
 */
const WAREHOUSE_CATALOG_PAGES = new Map<string, WarehousePageSegment>([
  ['Amazon_S3', 's3'],
  ['Google_Cloud_Storage', 'gcs'],
  ['Azure_Blob_Storage', 'azure_blob'],
  ['Amazon_Redshift', 'redshift'],
  ['Google_BigQuery', 'bigquery'],
  ['Snowflake', 'snowflake'],
]);

/**
 * The catalog ids the directory serves its own card for.
 *
 * Derived from the map so the filter and the link cannot disagree: an id added
 * to one is in the other.
 */
export const WAREHOUSE_CATALOG_IDS: readonly string[] = Object.freeze([...WAREHOUSE_CATALOG_PAGES.keys()]);

/**
 * The `/integrations/warehouse/:type` segment for a catalog id, or `null` when
 * the id is not one of the warehouse rows.
 *
 * The segment rather than a built path, so the caller can put it through
 * `generatePath` against the typed route and have a renamed or deleted warehouse
 * route fail typecheck instead of becoming a silent dead link — which is the
 * failure this whole module exists to fix.
 */
export function warehouseCatalogPageSegment(catalogId: string): WarehousePageSegment | null {
  return WAREHOUSE_CATALOG_PAGES.get(catalogId) ?? null;
}
